import { useEffect, useState } from 'react';
import { FileText } from 'lucide-react';
import { userAPI } from '../UserAPI';
import { useAtom } from 'jotai';
import { authenticationAtom } from "../State";
import { configuration } from '../Configuration';

import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@/components/ui/tooltip';

const baseUrl = configuration.serverUrl;

/**
 * Was in der Datei steckt, für den Tooltip — nach MIME-Typ, sonst nach Endung (eine vom Werkzeug
 * erzeugte Datei trägt beides, eine aus dem Speicher manchmal nur den Namen). Rein und exportiert,
 * damit die Regel testbar ist, ohne die Komponente zu rendern. Unbekanntes ist bewusst 'file' und
 * nicht etwa nicht-klickbar: dass wir einen Typ nicht benennen können, heißt nicht, dass der Nutzer
 * ihn nicht herunterladen darf.
 */
const IMAGE_EXTENSIONS = ['.png', '.jpg', '.jpeg', '.gif', '.svg', '.webp', '.tif', '.tiff'];

export const describeFileKind = (mimeType?: string, filename = ''): string => {
    const name = filename.toLowerCase();
    const mime = (mimeType || '').toLowerCase();
    const is = (m: string, ...extensions: string[]) =>
        mime === m || extensions.some(extension => name.endsWith(extension));

    if (is('application/pdf', '.pdf')) return 'PDF';
    if (is('application/vnd.openxmlformats-officedocument.wordprocessingml.document', '.docx', '.doc'))
        return 'Word document';
    if (is('application/vnd.openxmlformats-officedocument.presentationml.presentation', '.pptx', '.ppt'))
        return 'PowerPoint presentation';
    if (is('application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', '.xlsx', '.xls'))
        return 'Excel workbook';
    if (is('text/csv', '.csv')) return 'CSV file';
    if (mime.startsWith('image/') || IMAGE_EXTENSIONS.some(extension => name.endsWith(extension)))
        return 'image';
    if (is('text/markdown', '.md')) return 'Markdown file';
    if (is('text/plain', '.txt')) return 'text file';
    return 'file';
};

interface FileResourceProps {
    resource: any;
    resourceSelected?: (resource: any) => void;
    stableKey?: string;
}

export const FileResource = ({ resource, resourceSelected: _resourceSelected, stableKey: _stableKey }: FileResourceProps) => {
    const [authentication] = useAtom(authenticationAtom);
    const [fileResource, setFileResource] = useState(null) as any;

    const handleFileClick = () => {
        if (fileResource && fileResource.url) {
            const fullUrl = fileResource.url.startsWith('http')
                ? fileResource.url
                : `${baseUrl}/documents${fileResource.url}`;
            window.open(fullUrl, '_blank');
        }
    };

    useEffect(() => {
        const loadResource = async () => {
            let newResource: any = null;

            // Load resource from reference
            if (resource.reference) {
                newResource = await userAPI.getResource(authentication.customer, resource.reference);
            } else {
                newResource = resource;
            }

            console.log('New File resource >>>', newResource);
            setFileResource(newResource);
        };

        loadResource();
    }, [resource, authentication.customer]);

    if (!fileResource) {
        return null;
    }

    const fileName = fileResource.filename || fileResource.name || 'Unknown file';
    const fileSummary = fileResource.summary || fileResource.description || '';
    // Eine Datei mit URL lässt sich öffnen — die Typerkennung benennt nur noch, WAS sich öffnet.
    // Vorher entschied sie auch, OB: `isPDF || isWord || isPowerPoint || isExcel`, eine Positivliste,
    // in der u.a. `text/csv` fehlte. Ein Werkzeug, das eine CSV zurückgibt (asset.analyzePlan,
    // maintenanceCostEstimate), zeigte damit einen Chip, der nichts tut, obwohl die Datei korrekt
    // unter /documents/… ausgeliefert wird. Die Liste war falsch herum gedacht: nicht der Typ
    // entscheidet über den Download, sondern das Vorhandensein einer URL.
    const hasUrl = Boolean(fileResource.url);
    const opensOnClick = hasUrl;
    const fileKind = describeFileKind(fileResource.mimeType, fileName);

    const tooltipContent = (
        <div>
            <div className="mb-2 font-bold">{fileName}</div>
            {fileSummary && (
                <div>{fileSummary}</div>
            )}
            {opensOnClick && (
                <div className="mt-2 text-xs opacity-80">
                    Click to open {fileKind}
                </div>
            )}
        </div>
    );

    // Chip-Inhalt ist in beiden Varianten identisch; nur das Element wechselt,
    // damit die anklickbare Datei eine echte Schaltfläche ist. Der Chip trägt
    // dieselbe Sprache wie die Quellenliste: Monoschrift, Fläche aus der
    // Opazitätsleiter, kein Rahmen.
    const chipClassName =
        "oa-mono inline-flex min-w-0 max-w-[220px] items-center gap-1 rounded-md bg-[var(--oa-surface)] px-2 py-1 text-left font-mono text-[10px] leading-[1.4] text-[var(--oa-fg-3)] transition-colors hover:bg-[var(--oa-surface-2)] hover:text-[var(--oa-fg)]";
    const chipBody = (
        <>
            <FileText size={10} className="shrink-0" aria-hidden />
            <span className="min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap">{fileName}</span>
        </>
    );

    return (
        <TooltipProvider delayDuration={300} skipDelayDuration={0}>
            <Tooltip>
                <TooltipTrigger asChild>
                    {opensOnClick ? (
                        <button
                            type="button"
                            data-testid="chat-resource-file-open"
                            className={`${chipClassName} cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring`}
                            onClick={handleFileClick}
                        >
                            {chipBody}
                        </button>
                    ) : (
                        <div
                            data-testid="chat-resource-file"
                            className={`${chipClassName} cursor-help`}
                        >
                            {chipBody}
                        </div>
                    )}
                </TooltipTrigger>
                <TooltipContent sideOffset={8}>{tooltipContent}</TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
};
